import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { MinusIcon, PlusIcon } from '@glu/icons-react';
import useStyles from './NavLink.styles';

const propTypes = {
  alwaysOpen: PropTypes.bool,
  children: PropTypes.node.isRequired,
  /** If false, the nav will not close on click of link */
  closeNavOnClick: PropTypes.bool,
  dataQa: PropTypes.string,
  disabled: PropTypes.bool,
  divider: PropTypes.bool,
  href: PropTypes.string,
  active: PropTypes.bool,
  LinkComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  onClick: PropTypes.func,
  open: PropTypes.bool,
  navLinks: PropTypes.arrayOf(PropTypes.shape({
    /** typically a string, but can also be provided as a component */
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
    /** custom component to override the default sub menu component */
    menu: PropTypes.node,
    /** custom component rendered imediately after the children */
    footer: PropTypes.node,
    /** callback after clicked.  */
    onClick: PropTypes.func,
    /** used on top nav tabs. visual seperator of links. useful when links are images */
    divider: PropTypes.bool,
    /** display icon indicating child navLinks. required for setting styles correctly */
    hasIcon: PropTypes.bool,
    /** render children as plain markup instead of a button */
    isHeader: PropTypes.bool,
    /** toggle relative position of tabs so that sub menus can be styled */
    relativePosition: PropTypes.bool,
    /** toggle if tab receives background shading on hover.
     * typically used in combination with custom children components */
    shadeOnHover: PropTypes.bool,
    /** link href. not used if link is a parent of navLinks */
    href: PropTypes.string
  })),
  tier: PropTypes.number,
  toggleOpen: PropTypes.func,
  /** If true then will apply specific left offset for child links */
  hasIcon: PropTypes.bool,
  ExpandIcon: PropTypes.func,
  CollapseIcon: PropTypes.func
};

const defaultProps = {
  alwaysOpen: false,
  closeNavOnClick: true,
  dataQa: undefined,
  disabled: undefined,
  divider: false,
  active: undefined,
  href: null,
  LinkComponent: 'a',
  onClick: null,
  open: false,
  navLinks: null,
  tier: 0,
  toggleOpen: null,
  hasIcon: false,
  ExpandIcon: PlusIcon,
  CollapseIcon: MinusIcon
};

export const RootLink = ({
  alwaysOpen,
  children,
  classes,
  closeNavOnClick,
  dataQa,
  divider,
  disabled,
  LinkComponent: LinkComponentPassed,
  onClick,
  open,
  navLinks,
  tier,
  toggleOpen,
  hasIcon,
  ExpandIcon,
  CollapseIcon,
  ...rest
}) => {
  const isAnchor = !navLinks;
  const LinkComponent = useMemo(() => {
    /** a tags don't support the disabled attribute */
    if (disabled && LinkComponentPassed === 'a') return 'button';
    return LinkComponentPassed;
  }, [LinkComponentPassed, disabled]);

  if (isAnchor) {
    return (
      <LinkComponent
        className={classNames(
          classes.link,
          classes[`linkTier${tier}`],
          { [classes.linkDivided]: divider, [classes.hasIcon]: hasIcon }
        )}
        data-qa={dataQa}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {children}
      </LinkComponent>
    );
  }

  const iconClassName = classNames(
    classes.icon,
    tier > 0 ? classes.subLinkIcon : ''
  );

  return alwaysOpen
    ? <div className={`${classes.link} ${classes.textLink}`} data-qa={dataQa}>{children}</div> : (
      <button
        data-qa={dataQa}
        type="button"
        onClick={toggleOpen}
        className={classNames(
          classes.link,
          classes[`linkTier${tier}`],
          { [classes.linkDivided]: divider, [classes.hasIcon]: hasIcon }
        )}
        disabled={disabled}
      >
        {children}

        {open ? (
          <CollapseIcon className={iconClassName} title="Close" />
        ) : (
          <ExpandIcon className={iconClassName} title="Open" />
        )}
      </button>
    );
};

RootLink.propTypes = {
  ...propTypes,
  classes: PropTypes.objectOf(PropTypes.string).isRequired
};
RootLink.defaultProps = {
  ...defaultProps
};

const NavLink = (props) => {
  const {
    alwaysOpen,
    children,
    dataQa,
    active,
    LinkComponent,
    open,
    navLinks,
    tier,
    toggleOpen,
    closeNav,
    openedLinkPaddingBottom,
    hasIcon,
    ExpandIcon,
    CollapseIcon,
    ...rest
  } = props;
  const [indexOfOpenSublink, setIndexOfOpenSublink] = useState(-1);
  const classes = useStyles(
    useMemo(() => ({ openedLinkPaddingBottom, navLinks }), [openedLinkPaddingBottom, navLinks])
  );

  const toggleOpenSublink = index => setIndexOfOpenSublink(prev => (index === prev ? -1 : index));

  return (
    <li className={classNames(classes.root, {
      [classes.rootOpen]: open,
      [classes.rootActive]: active,
      [classes.lastChildTier]: !tier
    })}
    >
      <RootLink
        navLinks={navLinks}
        toggleOpen={toggleOpen}
        alwaysOpen={alwaysOpen}
        classes={classes}
        dataQa={`${dataQa}-tier-${tier}`}
        open={open}
        tier={tier}
        LinkComponent={LinkComponent}
        ExpandIcon={ExpandIcon}
        CollapseIcon={CollapseIcon}
        {...rest}
      >
        {children}
      </RootLink>

      {navLinks ? (
        <ul
          className={classNames(
            classes.navLinks,
            { [classes.openLinks]: open || alwaysOpen }
          )}
        >
          {(open || alwaysOpen) && navLinks.map(({ closeNavOnClick = true, ...subLink }, index) => (
            <NavLink
              dataQa={dataQa}
              key={`side-navigation-root-${index}-${tier + 1}`} // eslint-disable-line react/no-array-index-key
              navLinks={subLink.navLinks}
              open={index === indexOfOpenSublink}
              LinkComponent={LinkComponent}
              tier={tier + 1}
              toggleOpen={() => toggleOpenSublink(index)}
              openedLinkPaddingBottom={openedLinkPaddingBottom}
              hasIcon={hasIcon}
              ExpandIcon={ExpandIcon}
              CollapseIcon={CollapseIcon}
              {...subLink}
              closeNav={closeNav}
              onClick={(...args) => {
                if (closeNavOnClick) {
                  closeNav(false);
                }
                if (typeof subLink.onClick === 'function') {
                  subLink.onClick(...args);
                }
              }}
            >
              {subLink.children}
            </NavLink>
          ))}
        </ul>
      ) : null}
    </li>
  );
};

NavLink.propTypes = {
  ...propTypes,
  closeNav: PropTypes.func,
  /** Padding bottom size for the opened links, default 13px */
  openedLinkPaddingBottom: PropTypes.number
};
NavLink.defaultProps = {
  ...defaultProps,
  openedLinkPaddingBottom: 13,
  closeNav: /* istanbul ignore next */ () => { }
};

export default NavLink;
